import type MsgReader from '@kenjiuno/msgreader';
import type { FieldsData } from '@kenjiuno/msgreader';
import type { Attachment as PostalAttachment } from 'postal-mime';
import type { VeosMailAttachment } from '../../api/documentsService';
import type { EmailViewModel } from './EmailView';
import {
  base64ToBlob,
  base64ToUint8Array,
  uint8ArrayToBase64,
  isReceivedEmailContentType,
  mimeFromExtension,
} from './documentHelpers';

export interface ParsedReceivedEmail {
  email: EmailViewModel;
  /**
   * The sender's bare address (no display name), or `''` when it is unknown —
   * best-effort / unparseable payloads included. Not validated: an Exchange DN
   * (`/O=EXCHANGELABS/…`) is returned as-is, the caller decides whether it is a
   * usable e-mail address. Kept here rather than on `EmailViewModel` (the
   * registered `claimflow.emailView` contract) — consumed by the Email card's
   * "En réponse à" prefill of "À" (mahhp 22859).
   */
  fromAddress: string;
  /** True when only a best-effort/fallback view could be produced. */
  bestEffort: boolean;
  notice?: string;
}

/** A successfully parsed email: the view model + the bare sender address. */
interface ParsedEmailParts {
  email: EmailViewModel;
  fromAddress: string;
}

const EMAIL_FALLBACK_NOTICE =
  "Aperçu complet de l'e-mail indisponible — " +
  "utilisez « Ouvrir » pour consulter le fichier d'origine.";

/** Content types of an email carried *as an attachment* of another email. */
const OUTLOOK_MSG_CONTENT_TYPE = 'application/vnd.ms-outlook';
const EML_CONTENT_TYPE = 'message/rfc822';

/** `name` suffixed with `.ext`, unless it already ends with it (case-insensitive). */
function withExtension(name: string, ext: string): string {
  return name.toLowerCase().endsWith(`.${ext}`) ? name : `${name}.${ext}`;
}

/** "Name <email>" / "Name" / "email" from any name/email pair. */
function joinNameEmail(name: string | undefined, email: string | undefined): string {
  const n = name?.trim() ?? '';
  const e = email?.trim() ?? '';
  if (n && e) return `${n} <${e}>`;
  return n || e;
}

function recipientEmail(recip: FieldsData): string {
  return recip.email ?? recip.smtpAddress ?? recip.email1EmailAddress ?? '';
}

/**
 * Sender address, picked across the several sender address fields. An internal
 * Exchange sender's `senderEmail` is an X.500 DN (`/O=EXCHANGELABS/…`) while
 * the SMTP fields carry the real address, so the first candidate holding an
 * "@" wins (mahhp 22859). With no such candidate, the plain `??` chain is kept
 * (a DN-only message still yields its DN).
 */
function msgSenderAddress(data: FieldsData): string {
  const candidates = [
    data.senderEmail,
    data.senderSmtpAddress,
    data.sentRepresentingSmtpAddress,
  ];
  const smtp = candidates.map((c) => c?.trim() ?? '').find((c) => c.includes('@'));
  return (
    smtp ?? data.senderEmail ?? data.senderSmtpAddress ?? data.sentRepresentingSmtpAddress ?? ''
  );
}

/** Sender display, falling back across the several sender address fields. */
function formatMsgSender(data: FieldsData): string {
  return joinNameEmail(data.senderName, msgSenderAddress(data));
}

/** "To" recipients (or all, when none are explicitly typed "to"). */
function formatMsgRecipients(data: FieldsData): string {
  const recipients = data.recipients ?? [];
  const toRecipients = recipients.filter((r) => r.recipType === 'to');
  const chosen = toRecipients.length > 0 ? toRecipients : recipients;
  return chosen
    .map((r) => joinNameEmail(r.name, recipientEmail(r)))
    .filter(Boolean)
    .join(', ');
}

/** First available message date; empty string when none is present. */
function extractMsgDate(data: FieldsData): string {
  return (
    data.messageDeliveryTime ?? data.clientSubmitTime ?? data.creationTime ?? ''
  );
}

/**
 * Decide whether a parsed EML part is a real attachment worth surfacing.
 * Inline images embedded in (and referenced from) the HTML body are part of
 * the message, not standalone files — mirrors the `.msg` `isRealAttachment`.
 */
function isRealEmlAttachment(att: PostalAttachment): boolean {
  // postal-mime flags parts of a `multipart/related` body as `related`.
  if (att.related === true) return false;
  // An inline disposition carrying a `cid:` reference is a body image too.
  if (att.disposition === 'inline' && att.contentId) return false;
  return true;
}

/** Coerce a postal-mime attachment payload into raw bytes. */
function emlAttachmentBytes(content: ArrayBuffer | Uint8Array | string): Uint8Array {
  if (typeof content === 'string') return new TextEncoder().encode(content);
  if (content instanceof Uint8Array) return content;
  return new Uint8Array(content);
}

function mapEmlAttachment(att: PostalAttachment, index: number): VeosMailAttachment | null {
  const bytes = emlAttachmentBytes(att.content);
  if (bytes.byteLength === 0) return null;

  const baseName = att.filename || `piece-jointe-${String(index + 1)}`;
  // An attached message (`message/rfc822` part) keeps its type and gets an
  // `.eml` name, so the row opens it in-app as an email and files it into the
  // GED with a usable extension.
  const isAttachedMessage = att.mimeType.toLowerCase() === EML_CONTENT_TYPE;
  const name = isAttachedMessage ? withExtension(baseName, 'eml') : baseName;
  const contentType =
    att.mimeType || mimeFromExtension(name) || 'application/octet-stream';

  return {
    id: `eml-pj-${String(index)}`,
    name,
    contentType,
    data: uint8ArrayToBase64(bytes),
    size: bytes.byteLength,
  };
}

/**
 * Parse a `message/rfc822` (EML) payload into an `EmailViewModel` using a real
 * in-browser MIME parser. Walks the (possibly deeply nested) multipart tree,
 * prefers the HTML body over text, and surfaces non-inline parts (PDF, images,
 * …) as `VeosMailAttachment`s so they appear in "Pièces jointes".
 *
 * `postal-mime` is loaded lazily, *inside* this function, so it never runs on
 * page load — same pattern as the `.msg` `@kenjiuno/msgreader` branch. The raw
 * bytes are passed through so postal-mime decodes charset and
 * Content-Transfer-Encoding itself.
 */
async function parseEmlBytes(bytes: Uint8Array): Promise<ParsedEmailParts> {
  const { default: PostalMime } = await import('postal-mime');
  const parsed = await PostalMime.parse(bytes);

  const html = parsed.html?.trim() ?? '';
  const bodyIsHtml = html.length > 0;
  const body = bodyIsHtml ? html : (parsed.text ?? '').trim();

  const to = (parsed.to ?? [])
    .map((recip) => joinNameEmail(recip.name, recip.address))
    .filter(Boolean)
    .join(', ');

  const attachments: VeosMailAttachment[] = [];
  parsed.attachments.forEach((att, index) => {
    if (!isRealEmlAttachment(att)) return;
    const mapped = mapEmlAttachment(att, index);
    if (mapped) attachments.push(mapped);
  });

  return {
    email: {
      from: joinNameEmail(parsed.from?.name, parsed.from?.address),
      to,
      date: parsed.date ?? '',
      subject: parsed.subject ?? '',
      body,
      bodyIsHtml: bodyIsHtml || undefined,
      attachments,
    },
    fromAddress: parsed.from?.address?.trim() ?? '',
  };
}

/**
 * Decide whether a parsed attachment is a real file worth surfacing. Inline
 * images referenced by `cid:` and hidden attachments with no file name are
 * skipped; an embedded msg or a named file is kept.
 */
function isRealAttachment(att: FieldsData): boolean {
  // An embedded Outlook message is a real attachment, named or not.
  if (att.innerMsgContent === true) return true;
  const name = att.fileName ?? att.name ?? '';
  if (!name) return false;
  // Hidden inline image with a content-id reference → part of the HTML body.
  if (att.attachmentHidden && att.pidContentId) return false;
  return true;
}

/**
 * File name of an Outlook message embedded in a `.msg` (MsgReader attach
 * method 5, `innerMsgContent`). Such an attachment carries no `fileName` —
 * only `name`, the inner message's subject — but `getAttachment` names the
 * standalone `.msg` it burns `${att.name}.msg`: prefer that. When the inner
 * message has no display name the library would say "undefined.msg", so fall
 * back to a numbered name; the result always ends in `.msg`.
 */
function embeddedMsgName(
  att: FieldsData,
  libraryFileName: string | undefined,
  index: number,
): string {
  const fromLibrary = att.name?.trim() ? libraryFileName?.trim() : undefined;
  const base =
    fromLibrary || att.fileName?.trim() || `piece-jointe-${String(index + 1)}`;
  return withExtension(base, 'msg');
}

function mapMsgAttachment(
  reader: MsgReader,
  att: FieldsData,
  index: number,
): VeosMailAttachment | null {
  let data: string;
  let libraryFileName: string | undefined;
  try {
    // For an embedded message this is the inner message re-serialised
    // ("burned") into standalone `.msg` bytes, not a raw stream.
    const attachment = reader.getAttachment(att);
    if (attachment.content.length === 0) return null;
    data = uint8ArrayToBase64(attachment.content);
    libraryFileName = attachment.fileName;
  } catch {
    return null;
  }

  // An embedded Outlook message has no `fileName` and no `attachMimeTag`, so
  // it used to surface as an extension-less octet-stream that the browser
  // could only download under a GUID (mahhp 22844). Surface it as a real
  // `.msg` so the row opens it in-app as an email.
  if (att.innerMsgContent === true) {
    return {
      id: `msg-pj-${String(index)}`,
      name: embeddedMsgName(att, libraryFileName, index),
      contentType: OUTLOOK_MSG_CONTENT_TYPE,
      data,
    };
  }

  const name = att.fileName ?? att.name ?? `piece-jointe-${String(index + 1)}`;
  const contentType =
    att.attachMimeTag ?? mimeFromExtension(name) ?? 'application/octet-stream';

  return {
    id: `msg-pj-${String(index)}`,
    name,
    contentType,
    data,
  };
}

/** Map a parsed `.msg` (`FieldsData`) into the view model + sender address. */
function parseMsg(reader: MsgReader, fallbackSubject: string): ParsedEmailParts | null {
  const data = reader.getFileData();
  if (data.error) return null;

  const bodyHtml = data.bodyHtml?.trim() ?? '';
  const bodyText = data.body?.trim() ?? '';
  const bodyIsHtml = bodyHtml.length > 0;
  const body = bodyIsHtml ? bodyHtml : bodyText;

  const attachments: VeosMailAttachment[] = [];
  (data.attachments ?? []).forEach((att, index) => {
    if (!isRealAttachment(att)) return;
    const mapped = mapMsgAttachment(reader, att, index);
    if (mapped) attachments.push(mapped);
  });

  const email: EmailViewModel = {
    from: formatMsgSender(data),
    to: formatMsgRecipients(data),
    date: extractMsgDate(data),
    subject: data.subject?.trim() || fallbackSubject,
    body,
    bodyIsHtml: bodyIsHtml || undefined,
    attachments,
  };

  const hasContent =
    Boolean(data.subject?.trim()) ||
    Boolean(email.from) ||
    Boolean(body) ||
    attachments.length > 0;
  return hasContent ? { email, fromAddress: msgSenderAddress(data).trim() } : null;
}

/**
 * Produce a received-email view from a document's base64 content.
 *
 * - `message/rfc822` (EML, MIME) → full MIME parse with postal-mime (headers,
 *   HTML/text body, non-inline attachments). Falls back gracefully on failure.
 * - `application/vnd.ms-outlook` (.msg, OLE/CFB binary) → full parse with
 *   MsgReader (headers, body, attachments). Falls back gracefully on failure.
 *
 * Both `postal-mime` and `@kenjiuno/msgreader` (with its iconv-lite/safer-buffer
 * chain) are loaded lazily, *inside* their branch, so neither runs on page load
 * — if the dynamic import or parse throws for any reason, we fall back to the
 * original file rather than crashing the page.
 */
export async function parseReceivedEmail(
  base64: string,
  contentType: string,
  fileName: string,
  fallbackSubject: string,
): Promise<ParsedReceivedEmail> {
  const lower = contentType.toLowerCase();

  if (lower.includes('message/rfc822')) {
    try {
      const bytes = base64ToUint8Array(base64);
      const { email, fromAddress } = await parseEmlBytes(bytes);
      const hasContent =
        email.subject || email.from || email.body || email.attachments.length > 0;
      if (hasContent) {
        return { email, fromAddress, bestEffort: false };
      }
    } catch {
      // import failure or parse failure → fall through to fallback below
    }
  } else if (lower.includes('application/vnd.ms-outlook')) {
    try {
      const { default: MsgReader } = await import('@kenjiuno/msgreader');
      const bytes = base64ToUint8Array(base64);
      // MsgReader takes an ArrayBuffer; pass exactly the attachment's bytes.
      const buffer = bytes.buffer.slice(
        bytes.byteOffset,
        bytes.byteOffset + bytes.byteLength,
      ) as ArrayBuffer;
      const reader = new MsgReader(buffer);
      const parsed = parseMsg(reader, fallbackSubject);
      if (parsed) {
        return { ...parsed, bestEffort: false };
      }
    } catch {
      // import failure or parse failure → fall through to fallback below
    }
  }

  // .msg/EML we could not parse: graceful fallback to the original file.
  const fallbackAttachment: VeosMailAttachment = {
    id: 'original',
    name: fileName || 'email.msg',
    contentType: contentType || 'application/vnd.ms-outlook',
    data: base64,
  };

  return {
    bestEffort: true,
    notice: EMAIL_FALLBACK_NOTICE,
    fromAddress: '',
    email: {
      from: '',
      to: '',
      date: '',
      subject: fallbackSubject,
      body: '',
      attachments: [fallbackAttachment],
    },
  };
}

/** True when this content type needs the received-email parser. */
export function isReceivedEmail(contentType: string | undefined | null): boolean {
  return isReceivedEmailContentType(contentType);
}

/** Build a Blob for a received email's raw original (used by the open action). */
export function receivedEmailBlob(base64: string, contentType: string): Blob {
  return base64ToBlob(base64, contentType);
}
